/**
 * Assembly list — displays selected device parts.
 * Used both in the sidebar (compact) and the main view.
 *
 * Items within each catalog group are sorted to match the anatomical
 * order used by PartSelectorList (proximal → left side → right side → other).
 */

import { AlertTriangle, Trash2, X } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IAssemblyEntry, ICatalogDescriptor } from '@/workflows/vascular/common/catalog';
import { groupAndSortAssembly } from '@/workflows/vascular/common/catalog';

// ============================================================================
// Component
// ============================================================================

interface AssemblyListProps {
    assembly: readonly IAssemblyEntry[];
    onRemove?: (instanceId: string) => void;
    onClearCatalog?: (catalogId: string) => void;
    compact?: boolean;
    /** Catalog descriptors for grouping entries by manufacturer. */
    catalogDescriptors?: readonly ICatalogDescriptor[];
}

export function AssemblyList({
    assembly,
    onRemove,
    onClearCatalog,
    compact,
    catalogDescriptors,
}: AssemblyListProps) {
    // Group entries by catalogId, sorted within each group by anatomical order.
    // Hook must be called unconditionally (before any early return).
    const groupedEntries = useMemo(() => groupAndSortAssembly(assembly), [assembly]);

    if (assembly.length === 0) {
        return <p className="px-3 py-2 text-xs text-muted-foreground italic">No devices selected yet.</p>;
    }

    const getCatalogLabel = (catalogId: string): string | null => {
        if (!catalogDescriptors) return null;
        const desc = catalogDescriptors.find((d) => d.id === catalogId);
        return desc ? `${desc.manufacturer} — ${desc.model}` : null;
    };

    return (
        <div className="flex flex-col">
            {[...groupedEntries.entries()].map(([catalogId, entries]) => {
                const label = getCatalogLabel(catalogId);
                return (
                    <div key={catalogId}>
                        {label && (
                            <div className="flex items-center px-2 py-1 bg-muted/40 border-b border-border/30">
                                <span className="text-[10px] font-semibold text-muted-foreground flex-1">
                                    {label}
                                </span>
                                {onClearCatalog && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-4 w-4 p-0 text-muted-foreground hover:text-destructive"
                                        title="Remove all devices from this catalog"
                                        onClick={() => onClearCatalog(catalogId)}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                )}
                            </div>
                        )}
                        {entries.map((entry) => (
                            <AssemblyEntryRow
                                key={entry.instanceId}
                                entry={entry}
                                onRemove={onRemove}
                                compact={compact}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function AssemblyEntryRow({
    entry,
    onRemove,
    compact,
}: {
    entry: IAssemblyEntry;
    onRemove?: (instanceId: string) => void;
    compact?: boolean;
}) {
    const sideLabel = entry.side !== 'none' ? (entry.side === 'left' ? 'L' : 'R') : null;

    return (
        <div
            className={cn(
                'flex items-center gap-1.5 border-b border-border/30 text-xs',
                compact ? 'px-2 py-1' : 'px-3 py-1.5',
                entry.stale && 'bg-amber-50/50'
            )}
        >
            {entry.stale && (
                <span title="Measurements changed">
                    <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                </span>
            )}
            <span className="font-medium text-foreground truncate flex-1">
                {entry.count > 1 && <span className="text-muted-foreground mr-1">{entry.count}×</span>}
                {entry.partLabel}
                {sideLabel && <span className="ml-1 text-muted-foreground">({sideLabel})</span>}
            </span>
            <span
                className={cn(
                    'font-mono text-[10px] shrink-0',
                    entry.resolvedRef ? 'text-foreground' : 'text-muted-foreground'
                )}
            >
                {entry.resolvedRef ?? '—'}
            </span>
            {onRemove && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => onRemove(entry.instanceId)}
                >
                    <Trash2 className="h-3 w-3" />
                </Button>
            )}
        </div>
    );
}
